// File: src/components/AddProductDialog.tsx
// FINAL CLEAN VERSION. Rebuilt with a correct structure and integrated with the theme.

import React, {useEffect, useState} from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';
import ImageUpload from '../ImageUpload';
import {createProduct} from '../../services/api';

// The interface defines the component's props
interface AddProductDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

// Define the shape of the form data
const initialState = {
    name: '',
    description: '',
    brand: '',
    images: [] as string[],
};

const AddProductDialog: React.FC<AddProductDialogProps> = ({open, onClose, onSuccess}) => {
    const {t} = useTranslation();
    const [formData, setFormData] = useState(initialState);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset form when the dialog opens
    useEffect(() => {
        if (open) {
            setFormData(initialState);
            setError(null);
        }
    }, [open]);

    const handleInputChange = (field: keyof typeof initialState) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({...formData, [field]: event.target.value});
    };

    const handleImagesChange = (newImages: string[]) => {
        setFormData({...formData, images: newImages});
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            setError(t('products.add.name_required'));
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await createProduct(formData);
            onSuccess();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('products.add.unknown_error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {t('products.add.title')}
                <IconButton
                    aria-label={t('products.add.close')}
                    onClick={onClose}
                    sx={{position: 'absolute', right: 8, top: 8}}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                )}
                <TextField
                    autoFocus
                    required
                    margin="dense"
                    label={t('products.add.product_name')}
                    fullWidth
                    value={formData.name}
                    onChange={handleInputChange('name')}
                />
                <TextField
                    margin="dense"
                    label={t('products.add.brand')}
                    fullWidth
                    value={formData.brand}
                    onChange={handleInputChange('brand')}
                />
                <TextField
                    margin="dense"
                    label={t('products.add.description')}
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange('description')}
                />
                <ImageUpload
                    images={formData.images}
                    onImagesChange={handleImagesChange}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {t('products.add.cancel')}
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? <CircularProgress size={24}/> : t('products.add.create_product')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddProductDialog;
